import React from 'react';

import { UnifiedDialogEntry } from '../../../../types';

import type { ComposeToolbarType } from './InputTypes/types';
import EmailInput from './InputTypes/EmailInput';
import InternalNoteInput from './InputTypes/InternalNoteInput';
import SMSInput from './InputTypes/SMSInput';

type ComposeInputRendererProps = {
  composeType: ComposeToolbarType;
  customerId: number | undefined;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  yelpLeadKey?: string | null;
  hasPermanentPhone?: boolean;
  temporaryPhone?: string | null;
  initialText?: string | null;
  initialSubject?: string | null;
  onSmsSent?: () => void;
  onSuccess?: () => void;
  toValidationError?: string | null;
  onAddOptimisticMessage?: (entry: UnifiedDialogEntry) => void;
  onUpdateOptimisticMessage?: (optimisticId: string, updates: Partial<UnifiedDialogEntry>) => void;
  onRemoveOptimisticMessage?: (optimisticId: string) => void;
};

const ComposeInputRenderer: React.FC<ComposeInputRendererProps> = ({
  composeType,
  customerId,
  customerName,
  customerPhone,
  customerEmail,
  yelpLeadKey,
  hasPermanentPhone = false,
  temporaryPhone,
  initialText,
  initialSubject,
  onSmsSent,
  onSuccess,
  toValidationError,
  onAddOptimisticMessage,
  onUpdateOptimisticMessage,
  onRemoveOptimisticMessage,
}) => {
  // Hide input when no type selected
  if (composeType === null) {
    return null;
  }

  switch (composeType) {
    case 'SMS':
      // For Yelp-only customers (have yelp_lead_key but no permanent phone), use Yelp carrier
      if (yelpLeadKey && !hasPermanentPhone) {
        return (
          <SMSInput
            carrier="yelp"
            customerId={customerId}
            customerName={customerName}
            initialText={initialText}
            onAddOptimisticMessage={onAddOptimisticMessage}
            onRemoveOptimisticMessage={onRemoveOptimisticMessage}
            onSuccess={onSmsSent || onSuccess}
            onUpdateOptimisticMessage={onUpdateOptimisticMessage}
            temporaryPhone={temporaryPhone}
            toValidationError={toValidationError}
            yelpLeadKey={yelpLeadKey} />
        );
      }
      return (
        <SMSInput
          customerId={customerId}
          customerName={customerName}
          customerPhone={customerPhone}
          initialText={initialText}
          onAddOptimisticMessage={onAddOptimisticMessage}
          onRemoveOptimisticMessage={onRemoveOptimisticMessage}
          onSuccess={onSmsSent || onSuccess}
          onUpdateOptimisticMessage={onUpdateOptimisticMessage}
          toValidationError={toValidationError} />
      );
    case 'Yelp':
      return hasPermanentPhone ? (
        <SMSInput
          customerId={customerId}
          customerName={customerName}
          customerPhone={customerPhone}
          initialText={initialText}
          onAddOptimisticMessage={onAddOptimisticMessage}
          onRemoveOptimisticMessage={onRemoveOptimisticMessage}
          onSuccess={onSmsSent || onSuccess}
          onUpdateOptimisticMessage={onUpdateOptimisticMessage}
          toValidationError={toValidationError} />
      ) : (
        <SMSInput
          carrier="yelp"
          customerId={customerId}
          customerName={customerName}
          initialText={initialText}
          onAddOptimisticMessage={onAddOptimisticMessage}
          onRemoveOptimisticMessage={onRemoveOptimisticMessage}
          onSuccess={onSmsSent || onSuccess}
          onUpdateOptimisticMessage={onUpdateOptimisticMessage}
          temporaryPhone={temporaryPhone}
          toValidationError={toValidationError}
          yelpLeadKey={yelpLeadKey} />
      );
    case 'Email':
      return (
        <EmailInput
          customerEmail={customerEmail}
          customerId={customerId}
          customerName={customerName}
          initialSubject={initialSubject}
          onAddOptimisticMessage={onAddOptimisticMessage}
          onRemoveOptimisticMessage={onRemoveOptimisticMessage}
          onSuccess={onSuccess}
          onUpdateOptimisticMessage={onUpdateOptimisticMessage} />
      );
    case 'Notes':
      return (
        <InternalNoteInput
          customerId={customerId}
          onAddOptimisticMessage={onAddOptimisticMessage}
          onRemoveOptimisticMessage={onRemoveOptimisticMessage}
          onSuccess={onSuccess}
          onUpdateOptimisticMessage={onUpdateOptimisticMessage} />
      );
  }
};

export default ComposeInputRenderer;
